import React, { useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Drawer,
  Tooltip,
  Popconfirm,
  Badge,
  Descriptions,
  Alert,
  message,
  Spin,
  Empty,
  Switch,
  Progress,
} from 'antd';
import {
  PlayCircleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  HistoryOutlined,
  ReloadOutlined,
  SyncOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { jobApi, JobInfo, JobRun, JobRunStatus } from '../lib/api';

const { Title, Text } = Typography;

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

const STATUS_COLOR: Record<JobRunStatus, string> = {
  running: 'processing',
  success: 'success',
  failed: 'error',
  skipped: 'warning',
};

const STATUS_ICON: Record<JobRunStatus, React.ReactNode> = {
  running: <SyncOutlined spin />,
  success: <CheckCircleOutlined />,
  failed: <CloseCircleOutlined />,
  skipped: <PauseCircleOutlined />,
};

function StatusTag({ status }: { status?: JobRunStatus | null }) {
  if (!status) return <Tag color="default">Never run</Tag>;
  return (
    <Tag color={STATUS_COLOR[status]} icon={STATUS_ICON[status]}>
      {status.toUpperCase()}
    </Tag>
  );
}

function formatDateSafe(value?: string | null): string {
  if (!value) return '—';
  try {
    return format(parseISO(value), 'yyyy-MM-dd HH:mm:ss');
  } catch {
    return value;
  }
}

function fromNow(value?: string | null): string {
  if (!value) return '—';
  try {
    return formatDistanceToNow(parseISO(value), { addSuffix: true });
  } catch {
    return value;
  }
}

// ---------------------------------------------------------------------------
// Run history drawer
// ---------------------------------------------------------------------------

interface RunHistoryDrawerProps {
  jobId: string | null;
  open: boolean;
  onClose: () => void;
}

const RunHistoryDrawer: React.FC<RunHistoryDrawerProps> = ({ jobId, open, onClose }) => {
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const { data, isLoading, error } = useQuery({
    queryKey: ['job-runs', jobId, page],
    queryFn: () => jobApi.getJobRuns(jobId!, pageSize, (page - 1) * pageSize),
    enabled: open && !!jobId,
    staleTime: 30_000,
  });

  const columns = [
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (s: JobRunStatus) => <StatusTag status={s} />,
    },
    {
      title: 'Started',
      dataIndex: 'started_at',
      key: 'started_at',
      width: 170,
      render: (v: string) => (
        <Tooltip title={formatDateSafe(v)}>
          <Text type="secondary">{fromNow(v)}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Duration',
      dataIndex: 'duration_seconds',
      key: 'duration_seconds',
      width: 100,
      render: (v?: number) => (v != null ? `${v.toFixed(1)}s` : '—'),
    },
    {
      title: 'Processed',
      dataIndex: 'records_processed',
      key: 'records_processed',
      width: 90,
      align: 'right' as const,
      render: (v: number) => <Text strong>{v}</Text>,
    },
    {
      title: 'Failed',
      dataIndex: 'records_failed',
      key: 'records_failed',
      width: 80,
      align: 'right' as const,
      render: (v: number) =>
        v > 0 ? <Text type="danger" strong>{v}</Text> : <Text type="secondary">0</Text>,
    },
    {
      title: 'Error',
      dataIndex: 'error_message',
      key: 'error_message',
      render: (v?: string) =>
        v ? (
          <Tooltip title={v}>
            <Text type="danger" ellipsis style={{ maxWidth: 220, display: 'inline-block' }}>
              {v}
            </Text>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  return (
    <Drawer
      title={
        <Space>
          <HistoryOutlined />
          <span>Run History — <code>{jobId}</code></span>
        </Space>
      }
      width={900}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" tip="Loading run history…" />
        </div>
      ) : error ? (
        <Alert type="error" message="Failed to load run history" showIcon />
      ) : !data || data.runs.length === 0 ? (
        <Empty description="No runs found for this job" />
      ) : (
        <Table<JobRun>
          dataSource={data.runs}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={{
            current: page,
            pageSize,
            total: data.total,
            onChange: setPage,
            showSizeChanger: false,
            showTotal: (total) => `${total} runs`,
          }}
        />
      )}
    </Drawer>
  );
};

// ---------------------------------------------------------------------------
// Main Jobs page
// ---------------------------------------------------------------------------

const Jobs: React.FC = () => {
  const queryClient = useQueryClient();
  const [historyJobId, setHistoryJobId] = useState<string | null>(null);

  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['admin-jobs'],
    queryFn: () => jobApi.listJobs(),
    staleTime: 30_000,
    refetchInterval: 15_000, // Auto-refresh every 15s to pick up running status
  });

  const triggerMutation = useMutation({
    mutationFn: (jobId: string) => jobApi.triggerJob(jobId),
    onSuccess: (result) => {
      message.success(`Job triggered — run ID: ${result.run_id}`);
      queryClient.invalidateQueries({ queryKey: ['admin-jobs'] });
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail ?? 'Failed to trigger job';
      message.error(detail);
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ jobId, enable }: { jobId: string; enable: boolean }) =>
      enable ? jobApi.enableJob(jobId) : jobApi.disableJob(jobId),
    onSuccess: (result) => {
      message.success(`Job ${result.enabled ? 'enabled' : 'disabled'}`);
      queryClient.invalidateQueries({ queryKey: ['admin-jobs'] });
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail ?? 'Failed to update job';
      message.error(detail);
    },
  });

  const jobs = data?.jobs ?? [];

  const columns = [
    {
      title: 'Job',
      dataIndex: 'job_id',
      key: 'job_id',
      width: 220,
      render: (id: string, row: JobInfo) => (
        <Space direction="vertical" size={2}>
          <Text strong style={{ fontFamily: 'monospace' }}>{id}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{row.description}</Text>
        </Space>
      ),
    },
    {
      title: 'Schedule',
      dataIndex: 'schedule',
      key: 'schedule',
      width: 180,
      render: (s: string) => (
        <Space>
          <ClockCircleOutlined style={{ color: '#1890ff' }} />
          <Text>{s}</Text>
        </Space>
      ),
    },
    {
      title: 'Last Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (s?: JobRunStatus) => <StatusTag status={s} />,
    },
    {
      title: 'Last Run',
      dataIndex: 'last_run_at',
      key: 'last_run_at',
      width: 150,
      render: (v?: string) => (
        <Tooltip title={formatDateSafe(v)}>
          <Text type="secondary">{fromNow(v)}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Duration',
      dataIndex: 'last_run_duration_seconds',
      key: 'last_run_duration_seconds',
      width: 90,
      align: 'right' as const,
      render: (v?: number) => (v != null ? `${v.toFixed(1)}s` : '—'),
    },
    {
      title: 'Processed',
      dataIndex: 'last_run_records_processed',
      key: 'last_run_records_processed',
      width: 95,
      align: 'right' as const,
      render: (v?: number) => (v != null ? <Text strong>{v}</Text> : '—'),
    },
    {
      title: 'Next Run',
      dataIndex: 'next_run_at',
      key: 'next_run_at',
      width: 150,
      render: (v?: string) =>
        v ? (
          <Tooltip title={formatDateSafe(v)}>
            <Badge status="processing" text={fromNow(v)} />
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 90,
      align: 'center' as const,
      render: (enabled: boolean, row: JobInfo) => (
        <Switch
          checked={enabled}
          loading={toggleMutation.isPending}
          onChange={(checked) =>
            toggleMutation.mutate({ jobId: row.job_id, enable: checked })
          }
          checkedChildren="ON"
          unCheckedChildren="OFF"
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: unknown, row: JobInfo) => (
        <Space>
          <Popconfirm
            title={`Trigger "${row.job_id}" now?`}
            description="The job will run in the background immediately."
            onConfirm={() => triggerMutation.mutate(row.job_id)}
            okText="Trigger"
            cancelText="Cancel"
          >
            <Tooltip title="Run now">
              <Button
                type="primary"
                size="small"
                icon={<PlayCircleOutlined />}
                loading={triggerMutation.isPending && triggerMutation.variables === row.job_id}
              />
            </Tooltip>
          </Popconfirm>
          <Tooltip title="Run history">
            <Button
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => setHistoryJobId(row.job_id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const runningCount = jobs.filter((j) => j.status === 'running').length;
  const failedCount = jobs.filter((j) => j.status === 'failed').length;

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 24 }} align="center">
        <Title level={3} style={{ margin: 0 }}>
          Background Jobs
        </Title>
        <Button
          icon={<ReloadOutlined spin={isFetching} />}
          onClick={() => refetch()}
          loading={isFetching}
        >
          Refresh
        </Button>
      </Space>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <Card size="small" style={{ minWidth: 140 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{jobs.length}</div>
            <Text type="secondary">Total Jobs</Text>
          </div>
        </Card>
        <Card size="small" style={{ minWidth: 140 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#52c41a' }}>
              {jobs.filter((j) => j.enabled).length}
            </div>
            <Text type="secondary">Enabled</Text>
          </div>
        </Card>
        {runningCount > 0 && (
          <Card size="small" style={{ minWidth: 140, borderColor: '#1890ff' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#1890ff' }}>
                <SyncOutlined spin /> {runningCount}
              </div>
              <Text type="secondary">Running</Text>
            </div>
          </Card>
        )}
        {failedCount > 0 && (
          <Card size="small" style={{ minWidth: 140, borderColor: '#ff4d4f' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#ff4d4f' }}>{failedCount}</div>
              <Text type="secondary">Last Failed</Text>
            </div>
          </Card>
        )}
      </div>

      {error && (
        <Alert
          type="error"
          message="Failed to load jobs"
          description={(error as any)?.message}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card bodyStyle={{ padding: 0 }}>
        <Table<JobInfo>
          dataSource={jobs}
          columns={columns}
          rowKey="job_id"
          loading={isLoading}
          size="middle"
          pagination={false}
          expandable={{
            expandedRowRender: (row) =>
              row.last_run_error ? (
                <Alert
                  type="error"
                  message="Last run error"
                  description={row.last_run_error}
                  showIcon
                />
              ) : null,
            rowExpandable: (row) => !!row.last_run_error,
          }}
        />
      </Card>

      <RunHistoryDrawer
        jobId={historyJobId}
        open={!!historyJobId}
        onClose={() => setHistoryJobId(null)}
      />
    </div>
  );
};

export default Jobs;

